import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import {
  FaArrowRight,
  FaTrophy,
  FaRunning,
  FaUsers,
  FaHeartbeat,
  FaMedal,
  FaStar,
} from "react-icons/fa";

// ================= SPORTS IMAGES =================

import skatingImg from "../assets/skats1.jpeg";
import chessImg from "../assets/chess1.jpeg";
import yudoImg from "../assets/yudo1.jpeg";
import basketballImg from "../assets/basket2.jpeg";
import tableTennisImg from "../assets/tabletennis1.jpeg";
import khoKhoImg from "../assets/khokho1.jpeg";
import hockeyImg from "../assets/hockey1.jpeg";
import badmintonImg from "../assets/badminton.jpeg";
import swimmingImg from "../assets/swimming.jpeg";
import cricketImg from "../assets/cri2.jpeg";
import shootingImg from "../assets/shoot2.jpeg";

// ================= SPORTS DATA =================

const sports = [
  {
    name: "Skating",
    image: skatingImg,
    number: "01",
    description:
      "Students develop balance, coordination, confidence and physical fitness through skating.",
  },

  {
    name: "Chess",
    image: chessImg,
    number: "02",
    description:
      "Chess develops concentration, strategic thinking, patience and decision-making skills.",
  },

  {
    name: "Judo",
    image: yudoImg,
    number: "03",
    description:
      "Yudo encourages discipline, self-control, strength and confidence through structured training.",
  },

  {
    name: "Basketball",
    image: basketballImg,
    number: "04",
    description:
      "Basketball builds teamwork, agility, coordination and a strong competitive spirit.",
  },

  {
    name: "Table Tennis",
    image: tableTennisImg,
    number: "05",
    description:
      "Table tennis improves reflexes, concentration, coordination and quick decision-making.",
  },

  {
    name: "Kho-Kho",
    image: khoKhoImg,
    number: "06",
    description:
      "Kho-Kho develops speed, teamwork, alertness and physical endurance.",
  },

  {
    name: "Hockey",
    image: hockeyImg,
    number: "07",
    description:
      "Hockey encourages teamwork, stamina, discipline and sporting excellence.",
  },

  {
    name: "Badminton",
    image: badmintonImg,
    number: "08",
    description:
      "Badminton develops agility, speed, concentration, coordination and quick reflexes while encouraging healthy competition.",
  },

  {
    name: "Swimming",
    image: swimmingImg,
    number: "09",
    description:
      "Swimming promotes physical fitness, endurance, confidence and water safety while encouraging an active and healthy lifestyle.",
  },

  {
    name: "Cricket",
    image: cricketImg,
    number: "10",
    description:
      "Cricket develops teamwork, concentration, coordination, discipline and strategic thinking while encouraging sportsmanship.",
  },

  {
    name: "Shooting",
    image: shootingImg,
    number: "11",
    description:
      "Shooting develops focus, concentration, patience, discipline and mental control while promoting precision and confidence.",
  },
];

// ================= PAGE =================

export default function Sports() {
  return (
    <main className="bg-[#f7f9fc] text-[#07294d] overflow-hidden">

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="relative min-h-[70vh] bg-[#07294d] flex items-center overflow-hidden">

        {/* Decorative Elements */}

        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full border-[70px] border-[#d4af37]/10" />

        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full border-[80px] border-white/5" />

        <div className="absolute top-1/2 right-[15%] w-3 h-3 rounded-full bg-[#d4af37]" />

        <div className="absolute top-[30%] right-[8%] w-2 h-2 rounded-full bg-white/40" />

        <div className="absolute bottom-[25%] left-[8%] w-2 h-2 rounded-full bg-[#d4af37]/60" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full">

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >

            <div className="flex items-center gap-4 mb-7">

              <span className="w-12 h-[2px] bg-[#d4af37]" />

              <p className="uppercase tracking-[5px] text-[#d4af37] font-bold text-sm">
                Cambridge International School
              </p>

            </div>

            <h1 className="text-5xl md:text-7xl font-black text-white leading-[1.05]">

              Sports &

              <br />

              <span className="text-[#d4af37]">
                Physical Education
              </span>

            </h1>

            <div className="w-24 h-1 bg-[#d4af37] rounded-full mt-8" />

            <p className="mt-8 max-w-2xl text-gray-300 text-lg md:text-xl leading-8">
              Sports are an essential part of holistic education at
              Cambridge International School. We nurture discipline,
              leadership, teamwork and physical fitness through
              structured sports programs and healthy competition.
            </p>

            <div className="flex flex-wrap gap-4 mt-10">

              <Link
                to="/contact"
                className="group px-7 py-4 rounded-xl bg-[#d4af37] text-[#07294d] font-bold flex items-center gap-3 hover:bg-white transition duration-300"
              >
                Explore Sports

                <FaArrowRight className="group-hover:translate-x-1 transition" />
              </Link>

              <Link
                to="/contact"
                className="px-7 py-4 rounded-xl border border-white/30 text-white font-bold hover:bg-white hover:text-[#07294d] transition duration-300"
              >
                Contact Us
              </Link>

            </div>

          </motion.div>


          {/* HERO TROPHY */}

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="hidden lg:block absolute right-16 bottom-16"
          >

            <div className="w-40 h-40 rounded-full border border-[#d4af37]/40 flex items-center justify-center">

              <div className="w-28 h-28 rounded-full bg-[#d4af37] flex items-center justify-center shadow-2xl">

                <FaTrophy className="text-[#07294d] text-5xl" />

              </div>

            </div>

          </motion.div>

        </div>
      </section>


      {/* =====================================================
          INTRO
      ===================================================== */}

      <section className="py-20 md:py-28">

        <div className="max-w-7xl mx-auto px-6">

          <div className="grid lg:grid-cols-[1.1fr_.9fr] gap-16 items-center">

            {/* LEFT */}

            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >

              <p className="uppercase tracking-[5px] text-[#d4af37] font-bold text-sm">
                Sports at Cambridge
              </p>

              <h2 className="mt-4 text-4xl md:text-5xl font-black leading-tight">

                Building Character

                <br />

                Through{" "}

                <span className="text-[#d4af37]">
                  Sports
                </span>

              </h2>

              <div className="w-20 h-1 bg-[#d4af37] rounded-full mt-7 mb-8" />

              <p className="text-gray-700 text-lg leading-8 mb-6">
                It is a firm conviction of Cambridge International School
                that physical activity through sports and games is an
                essential ingredient for the personality development of
                every student.
              </p>

              <p className="text-gray-700 text-lg leading-8 mb-6">
                We believe that academics and sports are compatible and
                complementary in realizing every child's full potential.
                Sports encourage teamwork, leadership, dedication,
                discipline, self-confidence and self-esteem.
              </p>

              <p className="text-gray-700 text-lg leading-8">
                Participation in sports equips students to face life's
                challenges confidently while promoting a healthy lifestyle
                and overall well-being.
              </p>

            </motion.div>


            {/* RIGHT */}

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="relative"
            >

              <div className="bg-[#07294d] rounded-[35px] p-8 md:p-10 shadow-2xl relative overflow-hidden">

                <div className="absolute -right-20 -top-20 w-52 h-52 rounded-full border-[35px] border-[#d4af37]/10" />

                <p className="text-[#d4af37] uppercase tracking-[4px] font-bold text-sm">
                  More Than A Game
                </p>

                <h3 className="text-3xl font-black text-white mt-3 mb-8">
                  What Students Learn
                </h3>

                <div className="space-y-5">

                  {[
                    {
                      icon: <FaUsers />,
                      title: "Teamwork",
                    },

                    {
                      icon: <FaRunning />,
                      title: "Discipline",
                    },

                    {
                      icon: <FaHeartbeat />,
                      title: "Healthy Lifestyle",
                    },

                    {
                      icon: <FaTrophy />,
                      title: "Leadership & Confidence",
                    },
                  ].map((item, index) => (

                    <motion.div
                      key={index}
                      whileHover={{ x: 8 }}
                      className="flex items-center gap-5 bg-white/10 rounded-2xl p-4 border border-white/10"
                    >

                      <div className="w-12 h-12 shrink-0 rounded-xl bg-[#d4af37] text-[#07294d] flex items-center justify-center text-xl">

                        {item.icon}

                      </div>

                      <h4 className="text-white text-lg font-bold">
                        {item.title}
                      </h4>

                    </motion.div>

                  ))}

                </div>

              </div>

            </motion.div>

          </div>

        </div>

      </section>


      {/* =====================================================
          SPORTS GALLERY
      ===================================================== */}

      <section className="py-24 bg-white relative overflow-hidden">

        <div className="absolute top-0 left-0 w-72 h-72 bg-[#d4af37]/5 rounded-full blur-3xl" />

        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#07294d]/5 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">

          {/* SECTION HEADING */}

          <div className="text-center max-w-3xl mx-auto mb-16">

            <p className="uppercase tracking-[5px] text-[#d4af37] font-bold text-sm">
              Our Activities
            </p>

            <h2 className="mt-4 text-4xl md:text-5xl font-black">

              Discover Our{" "}

              <span className="text-[#d4af37]">
                Sports
              </span>

            </h2>

            <div className="w-20 h-1 bg-[#d4af37] rounded-full mx-auto mt-7" />

            <p className="mt-6 text-gray-600 text-lg leading-8">
              A vibrant range of indoor and outdoor activities designed
              to help students stay active, confident and competitive.
            </p>

          </div>


          {/* SPORTS CARDS */}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">

            {sports.map((sport, index) => (

              <motion.article
                key={sport.name}
                initial={{ opacity: 0, y: 45 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.08,
                }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="group bg-white rounded-[28px] overflow-hidden border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-500"
              >

                {/* IMAGE */}

                <div className="relative h-[280px] overflow-hidden">

                  <img
                    src={sport.image}
                    alt={sport.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />

                  {/* Gradient */}

                  <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#07294d]/80 to-transparent" />

                  {/* NUMBER */}

                  <div className="absolute top-5 left-5 w-12 h-12 rounded-xl bg-white/95 backdrop-blur flex items-center justify-center font-black text-[#07294d] shadow-lg">
                    {sport.number}
                  </div>

                  {/* TITLE */}

                  <div className="absolute bottom-5 left-6">

                    <h3 className="text-2xl font-black text-white">
                      {sport.name}
                    </h3>

                  </div>

                </div>


                {/* CONTENT */}

                <div className="p-6">

                  <p className="text-gray-600 leading-7 min-h-[84px]">
                    {sport.description}
                  </p>

                  <div className="mt-5 pt-5 border-t border-gray-100">

                    <span className="text-xs uppercase tracking-[3px] font-bold text-[#d4af37]">
                      Activity
                    </span>

                  </div>

                </div>

              </motion.article>

            ))}

          </div>

        </div>

      </section>


      {/* =====================================================
          BENEFITS
      ===================================================== */}

      <section className="py-24 bg-[#f7f9fc]">

        <div className="max-w-7xl mx-auto px-6">

          <div className="grid lg:grid-cols-2 gap-14 items-center">

            {/* LEFT DARK CARD */}

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="relative"
            >

              <div className="bg-[#07294d] rounded-[35px] p-8 md:p-12 min-h-[470px] flex flex-col justify-center overflow-hidden relative">

                <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full border-[50px] border-[#d4af37]/10" />

                <div className="absolute -left-28 -bottom-28 w-72 h-72 rounded-full border-[50px] border-white/5" />

                <div className="relative z-10">

                  <div className="w-16 h-16 rounded-2xl bg-[#d4af37] text-[#07294d] flex items-center justify-center text-2xl mb-7">

                    <FaMedal />

                  </div>

                  <p className="uppercase tracking-[4px] text-[#d4af37] font-bold text-sm">
                    Benefits
                  </p>

                  <h2 className="text-4xl md:text-5xl font-black text-white mt-4 leading-tight">

                    Why Sports

                    <br />

                    Matter

                  </h2>

                  <p className="text-gray-300 leading-8 mt-6 max-w-md">
                    Every game teaches something beyond the playing field.
                    Students grow stronger, more confident and learn the
                    importance of working together.
                  </p>

                </div>

              </div>

            </motion.div>


            {/* RIGHT BENEFITS */}

            <div className="grid sm:grid-cols-2 gap-5">

              {[
                {
                  title: "Teamwork",
                  text: "Sports teach students cooperation, communication and mutual respect.",
                },

                {
                  title: "Leadership",
                  text: "Participation develops confidence, responsibility and decision-making.",
                },

                {
                  title: "Discipline",
                  text: "Regular practice encourages dedication, consistency and focus.",
                },

                {
                  title: "Healthy Lifestyle",
                  text: "Sports support physical fitness, mental well-being and overall development.",
                },

              ].map((item, index) => (

                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.08,
                  }}
                  viewport={{ once: true }}
                  whileHover={{ y: -7 }}
                  className="bg-white rounded-3xl p-7 border border-gray-100 shadow-md hover:shadow-xl transition-all"
                >

                  <div className="w-12 h-12 rounded-xl bg-[#d4af37]/15 text-[#07294d] flex items-center justify-center text-xl mb-5">

                    <FaStar />

                  </div>

                  <h3 className="text-xl font-black mb-3">
                    {item.title}
                  </h3>

                  <p className="text-gray-600 leading-7">
                    {item.text}
                  </p>

                </motion.div>

              ))}

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          SPORTS SPIRIT
      ===================================================== */}

      <section className="py-12 bg-[#d4af37]">

        <div className="max-w-7xl mx-auto px-6">

          <div className="flex flex-wrap justify-center md:justify-between items-center gap-8">

            <div className="flex items-center gap-4">

              <FaTrophy className="text-[#07294d] text-3xl" />

              <div>

                <p className="font-black text-[#07294d] text-xl">
                  Play
                </p>

                <p className="text-[#07294d]/70 text-sm">
                  With Purpose
                </p>

              </div>

            </div>


            <div className="hidden md:block w-px h-12 bg-[#07294d]/20" />


            <div className="flex items-center gap-4">

              <FaUsers className="text-[#07294d] text-3xl" />

              <div>

                <p className="font-black text-[#07294d] text-xl">
                  Work
                </p>

                <p className="text-[#07294d]/70 text-sm">
                  As A Team
                </p>

              </div>

            </div>


            <div className="hidden md:block w-px h-12 bg-[#07294d]/20" />


            <div className="flex items-center gap-4">

              <FaHeartbeat className="text-[#07294d] text-3xl" />

              <div>

                <p className="font-black text-[#07294d] text-xl">
                  Stay
                </p>

                <p className="text-[#07294d]/70 text-sm">
                  Healthy
                </p>

              </div>

            </div>


            <div className="hidden md:block w-px h-12 bg-[#07294d]/20" />


            <div className="flex items-center gap-4">

              <FaMedal className="text-[#07294d] text-3xl" />

              <div>

                <p className="font-black text-[#07294d] text-xl">
                  Aim
                </p>

                <p className="text-[#07294d]/70 text-sm">
                  For Excellence
                </p>

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          CTA
      ===================================================== */}

      <section className="py-24 bg-white">

        <div className="max-w-5xl mx-auto px-6 text-center">

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >

            <p className="uppercase tracking-[5px] text-[#d4af37] font-bold text-sm">
              Cambridge International School
            </p>

            <h2 className="text-4xl md:text-6xl font-black mt-5 leading-tight">

              Building Future{" "}

              <span className="text-[#d4af37]">
                Champions
              </span>

            </h2>

            <div className="w-20 h-1 bg-[#d4af37] rounded-full mx-auto mt-7" />

            <p className="mt-7 text-lg leading-8 text-gray-600 max-w-2xl mx-auto">
              At Cambridge International School, sports are not just
              activities — they are an integral part of nurturing confident,
              disciplined and well-rounded individuals.
            </p>

            <Link
              to="/admission-form"
              className="mt-9 inline-flex items-center gap-3 px-9 py-4 rounded-xl bg-[#07294d] text-white font-bold hover:bg-[#d4af37] hover:text-[#07294d] transition duration-300"
            >
              Contact Admissions

              <FaArrowRight />

            </Link>

          </motion.div>

        </div>

      </section>

    </main>
  );
}